export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;
// Keep progression data authoritative in normal play. Developer mode opts
// into testing shortcuts at the scene boundary without changing licences.
export const TEST_UNLOCK_ALL = false;
// The tactical field uses the full canvas width. The command rail was removed;
// selection details now live in hover tooltips, toasts and the field manual.
export const BATTLE_W = GAME_WIDTH;
export const BATTLE_H = 560;
export const UI_TOP_H = 56;
// The tactical command deck deliberately has enough height for the full tower
// card rack; the battlefield camera takes the remaining space.
export const UI_BOTTOM_H = 142;

/**
 * The single authored battlefield frame shared by Map Forge, mission previews
 * and live Battle. Keeping this boundary in one place prevents editor points
 * from being projected against a taller preview and then clipped or shifted
 * by the battle HUD camera.
 */
export const MAP_FIELD_BOTTOM = GAME_HEIGHT - UI_BOTTOM_H;
export const MAP_FRAME = {
  x: 0,
  y: UI_TOP_H,
  width: BATTLE_W,
  height: MAP_FIELD_BOTTOM - UI_TOP_H,
} as const;

/** Product release shown in the command deck and main menu. */
export const APP_VERSION = '0.1.19';

/** Slightly ease every hostile's current attack-power tuning. */
export const ENEMY_POWER_MULTIPLIER = 1.235;

/** Global hostile-health tuning; halved from 1.235 so mobs die twice as fast. */
export const ENEMY_HEALTH_MULTIPLIER = 1.235 * 0.5 * 1.2 * 1.15;
/** Normal mode keeps the existing campaign curve but removes 20% of its current hostile HP. */
export const NORMAL_ENEMY_HEALTH_MULTIPLIER = 0.8;

/** Reduce authored boss health by 30% for a clearer, more decisive boss window. */
export const BOSS_HP_MULTIPLIER = 0.7;
/** Normal mode gets an additional 20% boss-health reduction for a gentler campaign curve. */
export const NORMAL_BOSS_HP_MULTIPLIER = BOSS_HP_MULTIPLIER * 0.8;

/** Nightmare begins with a leaner command budget and earns less supply in combat. */
export const NIGHTMARE_STARTING_SUPPLY_MULTIPLIER = 0.82;
export const NIGHTMARE_EARNED_INCOME_MULTIPLIER = 0.8;
export const NIGHTMARE_BOSS_HP_MULTIPLIER = 0.84;

/** Give the command base a larger survivability buffer without changing tower output. */
export const BASE_INTEGRITY_MULTIPLIER = 2;

/** Shared achievement, boss and treasure toast presentation. */
export const NOTIFICATION_TOAST_DURATION_SECONDS = 4.2;
export const NOTIFICATION_TOAST_BACKGROUND_ALPHA = 0.8;

/** Global research DATA drop reduction; rewards remain whole points. */
export const DATA_REWARD_MULTIPLIER = 0.64;
export function scaleDataReward(amount: number): number {
  return Math.ceil(Math.max(0, amount) * DATA_REWARD_MULTIPLIER);
}

/** Total research DATA budget for a completed non-survival map. */
export const MAP_CLEAR_DATA_REWARD_BASE = 15;

/** Survival enemy health grows by this amount for each wave reached. */
export const SURVIVAL_HP_INCREASE_PER_WAVE = 0.10;

/** Global weapon tuning: increase the current 0.65 output by 25%. */
export const WEAPON_DAMAGE_MULTIPLIER = 0.65 * 1.25;
/** Normal campaign receives a further 30% output lift without changing Hard/Nightmare. */
export const NORMAL_WEAPON_DAMAGE_MULTIPLIER = 1.3;

/** Rolling Death ordnance receives a dedicated 50% damage lift. */
export const ROLLING_DEATH_DAMAGE_MULTIPLIER = 1.5;

export const COLORS = {
  bg: 0x07090d,
  panel: 0x0e141a,
  panelEdge: 0x1e2a34,
  text: 0xc8d4e0,
  textDim: 0x6a7a88,
  accent: 0x5ec8ff,
  warn: 0xff7a3c,
  danger: 0xff4a4a,
  good: 0x7dce5a,
  supply: 0xd4a24a,
  path: 0x2a333b,
  terrain: 0x121a20,
  pad: 0x1a2830,
  padHover: 0x2a3e4a,
  padBlocked: 0x3a2020,
  command: 0x5ec8ff,
  ballistic: 0x6ec6ff,
  thermal: 0xff7a3c,
  artillery: 0xd4a24a,
  energy: 0xb06cff,
  enemy: 0x7dce5a,
  enemyElite: 0xff5a5a,
  enemyShield: 0x6ec6ff,
  barrier: 0x9ad4ff,
};

export const DAMAGE = {
  BALLISTIC: 'BALLISTIC',
  THERMAL: 'THERMAL',
  EXPLOSIVE: 'EXPLOSIVE',
  ENERGY: 'ENERGY',
} as const;

export type DamageType = (typeof DAMAGE)[keyof typeof DAMAGE];

export const PRIORITY = [
  'FIRST',
  'LAST',
  'STRONGEST',
  'WEAKEST',
  'FASTEST',
  'ARMOURED',
  'SPECIAL',
] as const;

export type TargetPriority = (typeof PRIORITY)[number];

export const SAVE_KEY = 'apogee_hold_save_v1';
export const SAVE_VERSION = 1;

/** Existing campaign tuning applied before the current balance pass. */
export const EARNED_INCOME_MULTIPLIER = 0.9;

/** Current economy pass: raise the reduced credit income by 25%. */
export const CREDIT_REWARD_MULTIPLIER = 0.5;

/** Requested economy tuning: all earned coin/supply rewards are increased by 20%. */
export const COIN_REWARD_MULTIPLIER = 1.2;

/** Normal mode kill bounty boost; harder modes keep their existing economy. */
export const NORMAL_KILL_REWARD_MULTIPLIER = 1.3;

/** New sorties begin with their full authored supply reserve. */
export const STARTING_SUPPLY_MULTIPLIER = 1;

/** Flat supply reserve added to every new sortie's authored starting funds. */
export const STARTING_SUPPLY_BONUS = 100;

/** Normal-mode sorties open with a flat 500-credit command budget. */
export const NORMAL_STARTING_SUPPLY = 500;

/** Campaign command progression tops out at rank 50. */
export const MAX_COMMAND_RANK = 50;

/** Reduce post-sortie XP awards while the campaign curve is rebalanced. */
export const XP_REWARD_MULTIPLIER = 0.3;

/** Supply prices are intentionally doubled while we tune the campaign economy. */
export const SUPPLY_COST_MULTIPLIER = 2;

/** Upgrade-path prices are half their authored supply cost (builds stay full). */
export const UPGRADE_COST_MULTIPLIER = 0.5;

export function scaleSupplyCost(cost: number): number {
  return Math.max(0, Math.ceil(cost * SUPPLY_COST_MULTIPLIER));
}

export function scaleUpgradeCost(cost: number): number {
  return Math.max(0, Math.ceil(cost * SUPPLY_COST_MULTIPLIER * UPGRADE_COST_MULTIPLIER));
}
